import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Box, IconButton,
  Avatar, Menu, MenuItem, Tooltip, Badge,
} from '@mui/material';
import { LocalHospital, ArrowBack, ExitToApp, Notifications } from '@mui/icons-material';
import { authService, notificationService } from '../services/api';
import NotificationPanel from './NotificationPanel';

/**
 * Shared top navigation bar used across all authenticated pages.
 *
 * Props:
 *   title    {string}   – Page title shown in the centre-left of the bar
 *   onBack   {func}     – If provided, renders an ArrowBack button; otherwise shows the hospital icon
 *   noPrint  {bool}     – Adds "no-print" CSS class (for pages that print)
 *   actions  {node}     – Optional extra action buttons rendered between the title and the right icons
 */
const TopBar = ({ title, onBack, noPrint = false, actions }) => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();

  const [anchorEl, setAnchorEl] = useState(null);

  // ── Notification state ──────────────────────────────────────────────────────
  const [panelOpen, setPanelOpen]           = useState(false);
  const [notifications, setNotifications]   = useState([]);
  const [unreadCount, setUnreadCount]       = useState(0);
  const [notifLoading, setNotifLoading]     = useState(false);

  const fetchCount = useCallback(async () => {
    try {
      const res = await notificationService.getUnreadCount();
      setUnreadCount(res.data.count ?? 0);
    } catch { /* ignore — user may not be logged in yet */ }
  }, []);

  const fetchNotifications = useCallback(async () => {
    setNotifLoading(true);
    try {
      const res = await notificationService.getMy();
      setNotifications(res.data);
      setUnreadCount(res.data.length);
    } catch {
      setNotifications([]);
    } finally {
      setNotifLoading(false);
    }
  }, []);

  // Fetch count on mount + every 2 minutes
  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 120_000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  // Auto-open panel once per browser session if there are unread notifications
  useEffect(() => {
    if (sessionStorage.getItem('notifPanelShown') === '1') return;
    fetchCount().then(() => {
      // fetchCount updates unreadCount asynchronously — we check via a one-off call
      notificationService.getUnreadCount()
        .then(res => {
          if ((res.data.count ?? 0) > 0) {
            fetchNotifications();
            setPanelOpen(true);
            sessionStorage.setItem('notifPanelShown', '1');
          }
        })
        .catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPanel = () => {
    setPanelOpen(true);
    fetchNotifications();
  };

  const handleDismiss = async (id) => {
    try {
      await notificationService.dismiss(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  };

  const handleDismissAll = async () => {
    try {
      await notificationService.dismissAll();
      setNotifications([]);
      setUnreadCount(0);
    } catch { /* ignore */ }
  };
  // ───────────────────────────────────────────────────────────────────────────

  const handleLogout = async () => {
    sessionStorage.removeItem('notifPanelShown');
    await authService.logout();
    navigate('/');
  };

  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  const tooltipLabel = displayName ? `${displayName} (${user?.role})` : 'Account';

  return (
    <>
      <AppBar
        position="static"
        className={`medical-gradient${noPrint ? ' no-print' : ''}`}
      >
        <Toolbar>
          {onBack ? (
            <IconButton edge="start" color="inherit" onClick={onBack} sx={{ mr: 1 }}>
              <ArrowBack />
            </IconButton>
          ) : (
            <LocalHospital sx={{ fontSize: 40, mr: 2 }} />
          )}

          <Typography variant="h5" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            {title}
          </Typography>

          {actions && <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>{actions}</Box>}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Notifications">
              <IconButton color="inherit" size="large" onClick={openPanel}>
                <Badge badgeContent={unreadCount} color="error" showZero={false} max={99}>
                  <Notifications />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title={tooltipLabel}>
              <IconButton
                size="large"
                onClick={e => setAnchorEl(e.currentTarget)}
                color="inherit"
              >
                <Avatar sx={{ bgcolor: '#1565c0', width: 36, height: 36 }}>
                  {user?.firstName?.charAt(0)?.toUpperCase()}
                </Avatar>
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
            >
              <MenuItem disabled sx={{ opacity: '1 !important', fontWeight: 600 }}>
                {displayName} — {user?.role}
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ExitToApp sx={{ mr: 1 }} /> Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <NotificationPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        notifications={notifications}
        loading={notifLoading}
        onDismiss={handleDismiss}
        onDismissAll={handleDismissAll}
      />
    </>
  );
};

export default TopBar;
